import { useRef, useState } from 'react'
import {
  CompactPlan,
  ConfirmDeletionModal,
  Mtds,
  SinglePlan,
  type UpdatePlan,
  useDeletePlanMutation,
  useErrorToast,
  useGetPlanByIdQuery,
  useAuth,
  useUpdatePlanMutation
} from 'prove-common'
import { PlanCardContent } from '../plan-card-content/PlanCardContent'
import { Link } from 'react-router'
import { CopyPlanModal } from '../copy-plan-modal/CopyPlanModal'
import { ConfirmActivatePlanModal } from '../confirm-activate-plan-modal/ConfirmActivatePlanModal'
import { countMissingApprovals } from '../../utils/countMissingApprovals'
import EditPlanCard from '../edit-plan-card/EditPlanCard'
import {
  buildUpdatePlanBody,
  hasDirectPlanValues,
  isFullPlanShape
} from 'prove-common'

interface PlanCardProps {
  plan: SinglePlan | CompactPlan
  onSelect?: (plan: CompactPlan) => void
  showActions?: boolean
}

const hasPlanUpdateEtag = (
  plan: SinglePlan
): plan is SinglePlan & { etag: string } =>
  typeof plan.etag === 'string' && plan.etag.length > 0

export const PlanCard = ({
  plan,
  onSelect,
  showActions = true
}: PlanCardProps) => {
  const { isAdmin } = useAuth()
  const updatePlan = useUpdatePlanMutation()
  const deletePlan = useDeletePlanMutation()
  const showUpdateError = useErrorToast('oppdatere statusen på planen')
  const showDeleteError = useErrorToast('slette planen')
  const [isCopyModalOpen, setCopyModalOpen] = useState(false)
  const [isActivateModalOpen, setActivateModalOpen] = useState(false)
  const [isEditModalOpen, setEditModalOpen] = useState(false)
  const [isEditFetchPending, setEditFetchPending] = useState(false)
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)
  const actionLock = useRef(false)
  const [editFetchError, setEditFetchError] = useState(false)
  const [loadedPlan, setLoadedPlan] = useState<SinglePlan>()
  const [activationPlan, setActivationPlan] = useState<SinglePlan>()
  const [activateMissingApprovalCount, setActivateMissingApprovalCount] =
    useState(0)
  const planQuery = useGetPlanByIdQuery(plan.id, false)
  const fetchedPlan = planQuery.data
  const getActionPlan = async (): Promise<SinglePlan> => {
    const result = await planQuery.refetch()
    if (result.isError || !result.data) {
      throw new Error('Kunne ikke hente planen. Prøv igjen.')
    }
    if (!hasDirectPlanValues(result.data)) {
      throw new Error(
        'Kunne ikke oppdatere planen fordi noen planopplysninger mangler. Hent planen på nytt og prøv igjen.'
      )
    }
    setLoadedPlan(result.data)
    return result.data
  }
  const handleClick = () => {
    onSelect?.(plan as CompactPlan)
  }

  const updatePlanStatus = async (
    newStatus: UpdatePlan['status'],
    actionPlan?: SinglePlan
  ) => {
    const suppliedActionPlan = actionPlan
    if (actionLock.current) return
    actionLock.current = true
    try {
      const fullPlan = suppliedActionPlan ?? (await getActionPlan())
      if (!hasPlanUpdateEtag(fullPlan)) throw new Error('Planen mangler ETag.')
      await updatePlan.mutateAsync({
        planId: fullPlan.id,
        etag: fullPlan.etag,
        body: buildUpdatePlanBody(fullPlan, { status: newStatus })
      })
    } catch (error) {
      showUpdateError(error)
    } finally {
      actionLock.current = false
    }
  }

  const activatePlan = async () => {
    if (actionLock.current) return
    const planIsAvailableWithoutLookup =
      isFullPlanShape(plan) && hasDirectPlanValues(plan)
    actionLock.current = true
    try {
      const planToActivate = await getActionPlan()
      const missingApprovalCount = countMissingApprovals(planToActivate)
      if (missingApprovalCount > 0) {
        setActivationPlan(planToActivate)
        setActivateMissingApprovalCount(missingApprovalCount)
        setActivateModalOpen(true)
        if (!planIsAvailableWithoutLookup) {
          actionLock.current = false
        }
        return
      }
      if (!hasPlanUpdateEtag(planToActivate)) {
        throw new Error('Planen mangler ETag.')
      }
      await updatePlan.mutateAsync({
        planId: planToActivate.id,
        etag: planToActivate.etag,
        body: buildUpdatePlanBody(planToActivate, { status: 'active' })
      })
    } catch (error) {
      showUpdateError(error)
    } finally {
      actionLock.current = false
    }
  }

  const requestEdit = async () => {
    if (actionLock.current) return
    actionLock.current = true
    setEditFetchPending(true)
    setEditFetchError(false)
    try {
      await getActionPlan()
      setEditModalOpen(true)
    } catch {
      setEditFetchError(true)
    } finally {
      setEditFetchPending(false)
      actionLock.current = false
    }
  }

  // Collection cards do not resolve inherited permissions. Open the detail to manage the plan.
  const canEdit = showActions && 'canManage' in plan && plan.canManage === true
  const canCopy = showActions && 'canManage' in plan
  const canDelete = showActions && isAdmin && plan.etag != null

  const cardContent = (
    <PlanCardContent
      plan={plan}
      onClickEdit={canEdit ? () => void requestEdit() : undefined}
      onClickDuplicate={canCopy ? () => setCopyModalOpen(true) : undefined}
      onClickActivate={canEdit ? activatePlan : undefined}
      onClickDeactivate={
        canEdit ? () => void updatePlanStatus('deactivated') : undefined
      }
      onClickSetAsDraft={
        canEdit ? () => void updatePlanStatus('draft') : undefined
      }
      onClickDelete={canDelete ? () => setDeleteModalOpen(true) : undefined}
    />
  )

  return (
    <>
      {onSelect ? (
        <Mtds.Card
          as='button'
          className='bg-background-tinted py-4'
          onClick={handleClick}
        >
          {cardContent}
        </Mtds.Card>
      ) : (
        <Mtds.Card
          className='bg-background-tinted py-4'
          data-clickdelegatefor={`plan-link-${plan.id}`}
        >
          <Link id={`plan-link-${plan.id}`} to={`/plans/${plan.id}`} hidden />
          {cardContent}
        </Mtds.Card>
      )}
      {(loadedPlan ?? fetchedPlan) &&
        isEditModalOpen &&
        hasDirectPlanValues(loadedPlan ?? fetchedPlan) && (
          <EditPlanCard
            isOpen={isEditModalOpen}
            setOpen={setEditModalOpen}
            plan={(loadedPlan ?? fetchedPlan)!}
          />
        )}
      {editFetchError && (
        <Mtds.Alert data-color='danger'>
          <span role='alert'>Kunne ikke hente planen. Prøv igjen.</span>
          <Mtds.Button
            type='button'
            onClick={() => void requestEdit()}
            aria-label='Prøv igjen'
          >
            Prøv igjen
          </Mtds.Button>
        </Mtds.Alert>
      )}
      {isEditFetchPending && (
        <Mtds.Alert aria-busy='true'>Henter planen…</Mtds.Alert>
      )}
      <CopyPlanModal
        isOpen={isCopyModalOpen}
        setOpen={setCopyModalOpen}
        plan={plan}
      />
      <ConfirmActivatePlanModal
        open={isActivateModalOpen}
        onClose={() => {
          setActivateModalOpen(false)
          setActivationPlan(undefined)
        }}
        onConfirm={async () => {
          setActivateModalOpen(false)
          const confirmedPlan = activationPlan
          setActivationPlan(undefined)
          await updatePlanStatus('active', confirmedPlan)
        }}
        missingApprovalCount={activateMissingApprovalCount}
      />
      <ConfirmDeletionModal
        title={`Planen «${plan.description ?? plan.id}» vil bli permanent slettet.`}
        isOpen={isDeleteModalOpen}
        setOpen={setDeleteModalOpen}
        isPending={deletePlan.isPending}
        onConfirm={async () => {
          if (plan.etag == null) return

          await deletePlan
            .mutateAsync({ planId: plan.id, etag: plan.etag })
            .then(() => {
              Mtds.toast.success('Planen er slettet')
            })
            .catch((error) => {
              showDeleteError(error)
            })
        }}
      />
    </>
  )
}
